// ────────────────────────────────────────────────
// src/components/dashboard/StudentDashboard.js
// Hatch‑Egg + Countdown + Backpack (max 5 slots)
// FULLY‑FIXED version – uses usePetManager + setDoc when activating
// ────────────────────────────────────────────────
import React, { useState, useEffect } from "react";
import "./StudentDashboard.css";
import { getAuth, updatePassword } from "firebase/auth";
import {
  doc,
  setDoc,             
  updateDoc,
  collection,
  onSnapshot,
  addDoc,
  deleteDoc,
} from "firebase/firestore";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Cell,
} from "recharts";
import { db } from "../../firebase";
import { HATCH_DELAY, expNeeded } from "../../Pet/Pet";
import { useUserProfile } from "../../hooks/useUserProfile";
import { useQuizResults } from "../../hooks/useQuizResults";
import { usePetManager } from "../../hooks/usePetManager";
import { PetMediaBox } from "./PetMediaBox";

/* ---------- constants ---------- */
export const USERS_COLLECTION = "users";
export const QUIZ_RESULTS_SUBCOL = "quizResults";
const COLORS = ["#4ade80", "#38bdf8", "#f43f5e", "#facc15", "#a78bfa", "#34d399"];

const prettifySubject = (key) => {
  const m = key.match(/^(\w+)_g(\d+)$/i);
  return m ? `Grade ${m[2]} ${m[1][0].toUpperCase() + m[1].slice(1)}` : key;
};

/* ---------- Backpack Hook ---------- */
const useBackpack = (uid) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!uid) return;
    const col = collection(db, "users", uid, "backpack");
    const unsub = onSnapshot(col, (snap) => {
      setItems(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    return unsub;
  }, [uid]);
  return { items, loading };
};

/* ===================================================================== */
export default function StudentDashboard() {
  /* firebase user */
  const authUser = getAuth().currentUser;

  /* realtime hooks */
  const { profile, loading: profileLoading } = useUserProfile(authUser?.uid);
  const { performance, loading: quizLoading } = useQuizResults(authUser?.uid);
  const { pet,loading: petLoading, feed, drink } = usePetManager(authUser?.uid);
  const { items: backpack, loading: bpLoading } = useBackpack(authUser?.uid);

  /* local ui state */
  const [newPwd, setNewPwd] = useState("");
  const [pwdMsg, setPwdMsg] = useState("");
  const [hatching, setHatching] = useState(false);
  const [countdown, setCountdown] = useState(null); // ms left

  /* ----- password change ----- */
  const handlePwd = async () => {
    if (newPwd.length < 6) return setPwdMsg("Password must be at least 6 characters");
    try {
      await updatePassword(authUser, newPwd);
      setPwdMsg("Password updated ✓");
      setNewPwd("");
    } catch (e) {
      setPwdMsg(e.message);
    }
  };

  /* ----- start hatch ----- */
  const startHatch = async () => {
    if (!pet || pet.stage !== "egg" || pet.hatchAt) return;
    setHatching(true);
    try {
      await updateDoc(doc(db, "users", authUser.uid, "pets", "current"), {
        hatchAt: Date.now() + HATCH_DELAY,
      });
    } catch (e) {
      console.error(e);
    } finally {
      setHatching(false);
    }
  };

  /* ----- release active egg/pet to backpack ----- */
  const releaseActive = async () => {
    if (!pet) return;
    if (backpack.length >= 5) return alert("Backpack full (max 5)");
    try {
      const bpCol = collection(db, "users", authUser.uid, "backpack");
      await addDoc(bpCol, pet);
      await deleteDoc(doc(db, "users", authUser.uid, "pets", "current"));
    } catch (e) {
      console.error(e);
    }
  };

  /* ----- activate item from backpack ----- */
  const activateFromBackpack = async (item) => {
    if (pet) return alert("Release current pet/egg first.");
    try {
      await setDoc(doc(db, "users", authUser.uid, "pets", "current"), {
        ...item,
      }); // setDoc → create or overwrite
      await deleteDoc(doc(db, "users", authUser.uid, "backpack", item.id));
    } catch (e) {
      console.error(e);
    }
  };

  /* ----- countdown timer ----- */
  useEffect(() => {
    if (!pet || pet.stage !== "egg" || !pet.hatchAt) { setCountdown(null); return; }
    const id = setInterval(() => {
      const left = pet.hatchAt - Date.now();
      setCountdown(left > 0 ? left : 0);
      if (left <= 0) clearInterval(id);
    }, 1000);
    return () => clearInterval(id);
  }, [pet]);

  /* guards */
  if (!authUser) return <p>Please log in.</p>;
  if (profileLoading || quizLoading || petLoading || bpLoading || !profile)
    return <p style={{ color: "white", textAlign: "center" }}>Loading…</p>;

  /* ----- derive performance data ----- */
  const strong = [], weak = [], chart = [];
  Object.entries(performance).forEach(([key, d]) => {
    if (d.finalQuizScore == null || !d.finalQuizTotal) return;
    const pct = Math.round((d.finalQuizScore / d.finalQuizTotal) * 100);
    chart.push({ subject: prettifySubject(key), pct });
    if (pct >= 80) strong.push(prettifySubject(key));
    if (pct < 50) weak.push(prettifySubject(key));
  });

/* ---------- backpack consumables ---------- */
const consumeItem = async (docId, item) => {
  if (!authUser) return;

  try {
    switch (item.sku) {
      case "spring_water":            // +25 % thirst
        await drink();                // hook already caps at 100 %
        break;

      case "berry_snack":             // +25 % hunger + small EXP
        await feed();
        break;

      case "evolving_rock": {         // evolve to Teen (≥ Lv 20, baby only)
        if (!pet || pet.level < 20 || pet.stage !== "baby") {
          return alert("Your pet must be a Lv 20 Baby to use the Evolving Rock");
        }
        if (!window.confirm("Evolve your pet into its Teen form?")) return;

        const newStage  = "teen";
        const cap       = (s) => s[0].toUpperCase() + s.slice(1);
        const photoUrl  = `/images/Pet/teen/${cap(pet.element)}Teen.png`;

        await updateDoc(
          doc(db, "users", authUser.uid, "pets", "current"),
          { stage: newStage, photoUrl }
        );
        break;
      }

      default:
        return;
    }
    /* finally: decrease qty, or delete if it was the last one */
    const ref = doc(db, "users", authUser.uid, "backpack", docId);
    if (item.qty && item.qty > 1) {
      await updateDoc(ref, { qty: item.qty - 1 });
    } else {
    await deleteDoc(ref);
    }
    } catch (e) {
    console.error("useItem failed", e);
  }
};


  /* =================== RENDER =================== */
  return (
    <div className="student-dashboard-page">
      <h2 className="student-dashboard-title">Student Dashboard</h2>

      <div className="dashboard-container">
        {/* ---------- PROFILE ---------- */}
        <aside className="dashboard-profile-section">
          <h3>Your Profile</h3>
          {[
            "First Name",
            "Last Name",
            "Account Name",
            "Birth Date",
            "Parent Code",
          ].map((k) => (
            <p key={k}>
              <strong>{k}:</strong> {profile[camel(k)]}
            </p>
          ))}
          <p>
            <strong>Tokens:</strong> {profile.coins ?? 0}
          </p>

          <div className="dashboard-password-section">
            <h4>Password</h4>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handlePwd();
              }}
            >
              <input
                type="password"
                value={newPwd}
                onChange={(e) => setNewPwd(e.target.value)}
              />
              <button type="submit">Update</button>
            </form>
            {pwdMsg && <p>{pwdMsg}</p>}
          </div>
        </aside>

        {/* ---------- PERFORMANCE ---------- */}
        <section className="dashboard-performance-section">
          <h3>Performance</h3>
          <table className="performance-table">
            <thead>
              <tr>
                <th>Subject</th>
                <th>Time&nbsp;(min)</th>
                <th>Quick&nbsp;Quiz</th>
                <th>Score</th>
                <th>Total</th>
                <th>%</th>
              </tr>
            </thead>
            <tbody>
              {Object.keys(performance).length === 0 && (
                <tr>
                  <td colSpan={6} align="center">
                    No data
                  </td>
                </tr>
              )}
              {Object.entries(performance).map(
                ([key, d]) =>
                  d.finalQuizTotal && (
                    <tr key={key}>
                      <td>{prettifySubject(key)}</td>
                      <td>{d.timeSpent ?? "-"}</td>
                      <td>{d.quickQuizAttempts ?? "-"}</td>
                      <td>{d.finalQuizScore}</td>
                      <td>{d.finalQuizTotal}</td>
                      <td>
                        {Math.round(
                          (d.finalQuizScore / d.finalQuizTotal) * 100
                        )}%
                      </td>
                    </tr>
                  )
              )}
            </tbody>
          </table>

          {chart.length > 0 && (
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={chart}>
                <XAxis
                  dataKey="subject"
                  tick={{ fontSize: 11 }}
                  interval={0}
                  angle={-15}
                  textAnchor="end"
                />
                <YAxis domain={[0, 100]} />
                <Tooltip formatter={(v) => `${v}%`} />
                <Bar dataKey="pct">
                  {chart.map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
          <p>
            <strong>Strong:</strong> {strong.join(", ") || "None"}
          </p>
          <p>
            <strong>Weak:</strong> {weak.join(", ") || "None"}
          </p>
        </section>

        {/* ---------- PET & BACKPACK ---------- */}
        <aside className="dashboard-pet-section">
          <h3>Pet Area</h3>
          {pet ? (
            <>
              <PetMediaBox pet={pet} />

              {/* ⭐️ Name + Level display */}
              <p className="pet-name-level">
                {(() => {/* “Red Baby”, “Green Teen”, etc. */
                const cap = (s) => s[0].toUpperCase() + s.slice(1);
                return `${cap(pet.element)} ${cap(pet.stage)}  –  Lv ${pet.level}`;
                })()}
                </p>

              {/* hatch / countdown / release */}
              {pet.stage === "egg" && !pet.hatchAt && (
                <button
                  className="hatch-btn"
                  onClick={startHatch}
                  disabled={hatching}
                >
                  Hatch Egg
                </button>
              )}
              {pet.hatchAt && pet.stage === "egg" && (
                <p
                  className={
                    "hatch-countdown" +
                    (countdown !== null && countdown < 30_000 ? " alert" : "")
                  }
                >
                  {countdown !== null
                    ? `${Math.floor(countdown / 60000)}:${String(
                        Math.floor(countdown / 1000) % 60
                      ).padStart(2, "0")}`
                    : "0:00"}
                </p>
              )}

              <button className="release-btn" onClick={releaseActive}>
                Release to Backpack
              </button>
              
              <table className="pet-stats-table">
                 <tbody>
                     {/* Element – plain text */}
                        <tr>
             <td>Element</td>
             <td>{pet.element}</td>
             </tr>
            {/* Hunger bar */}
            <tr>
               <td>Hunger</td>
            <td><div className="stat-bar">
             <div
            className="stat-fill hunger"
            style={{ width: `${Math.round(pet.hunger)}%` }}
          />
          <span>{Math.round(pet.hunger)}%</span>
        </div>
      </td>
    </tr>

    {/* Thirst bar */}
    <tr>
      <td>Thirst</td>
      <td>
        <div className="stat-bar">
          <div
            className="stat-fill thirst"
            style={{ width: `${Math.round(pet.thirst)}%` }}
          />
          <span>{Math.round(pet.thirst)}%</span>
        </div>
      </td>
    </tr>

    {/* EXP bar – scaled to expNeeded(level) */}
<tr>
  <td>EXP</td>
  <td>
    <div className="stat-bar">
      {(() => {
        const needed  = expNeeded(pet.level);               // 👈 dynamic denominator
        const pct     = Math.min(100, (pet.exp / needed) * 100);
        return (
          <>
            <div className="stat-fill exp" style={{ width: `${pct}%` }} />
            <span>{Math.round(pet.exp)}/{needed}</span>
          </>
        );
      })()}
    </div>
  </td>
</tr>
{["HP","MP","ATK","MATK","DEF","MDEF","Evasion","Crit",].map((lbl) => (
  <tr key={lbl}>
    <td>{lbl}</td>
    <td>{pet[lbl.toLowerCase()] ?? "-"}</td>
  </tr>
))}
  </tbody>
</table>
</>
) : (
<p>No active pet / egg.</p>
)}

          {/* ---------- BACKPACK ---------- */}
          <h4 className="backpack-title">Backpack</h4>
          <div className="backpack-grid">
            {Array.from({ length: 5 }).map((_, i) => {
              const itm = backpack[i];
              return (
                <div
                  key={i}
                  className={"backpack-slot" + (itm ? " filled" : "")}
                  onClick={() => { if (!itm) return;
                  if (["spring_water", "berry_snack", "evolving_rock", "element_of_life"].includes(itm.sku)) {
                    consumeItem(itm.id, itm);           // consume item
                    } else if (!pet) {
                      activateFromBackpack(itm);      // egg or extra pet → equip
                      }
                    }}
                >
                {itm && itm.photoUrl && (
                  <>
                  <img src={itm.photoUrl} alt="item" />
                  {itm.qty ? (                       /* show qty if present */
                  <span className="slot-qty">{itm.qty}</span>
                  ) : null}
                  </>
                )}
                </div>
              );
            })}
          </div>
        </aside>
      </div>
    </div>
  );
}

function camel(label) {
  return label
    .replace(/[^a-zA-Z0-9]+(.)/g, (_, c) => c.toUpperCase())
    .replace(/^./, (c) => c.toLowerCase());
}
